import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, User, ImpersonatableUser } from '../lib/api';
import { Cache } from '../lib/cache';
import { TokenManager } from '../lib/token-manager';
import { requestSharedSession, serveSharedSession } from '../lib/sessionSync';

// CR-5275: login can short-circuit when the user's role requires MFA. The
// caller renders an OTP step UI and finishes by calling finalizeMfaLogin
// once the backend returns a real token from the MFA endpoints.
export type LoginResult =
  | { kind: 'success' }
  | { kind: 'mfa_setup_required'; sessionToken: string }
  | { kind: 'mfa_required'; sessionToken: string };

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  // CR-5275: complete the auth state from an MFA endpoint response.
  finalizeMfaLogin: (userData: User, authToken: string) => void;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  updateUser: (patch: Partial<User>) => void;
  // Impersonation
  isImpersonating: boolean;
  originalUser: User | null;
  startImpersonation: (
    targetUser: User | ImpersonatableUser,
    targetToken: string,
    originalToken: string,
    targetContextCompany?: { id: number; name: string },
    auditLogId?: number,
    targetContextGroup?: { id: number; name: string },
  ) => void;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Accesses the current authentication state including user, token, and impersonation.
 * Must be used within an {@link AuthProvider}.
 *
 * @returns Auth state with login/logout actions and impersonation controls
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provides authentication state to the component tree.
 * Manages login/logout, token persistence via {@link TokenManager}, and user impersonation.
 * On mount, validates the stored token against the backend.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize state from secure storage
  const [user, setUser] = useState<User | null>(() => {
    const cached = TokenManager.getUser<User>();
    if (cached?.platform && !cached.company_type) {
      cached.company_type = cached.platform.toLowerCase() as User['company_type'];
    }
    // Set cache scope to persisted user on page refresh
    if (cached?.id) {
      Cache.setUserScope(cached.id);
    }
    return cached;
  });
  const [token, setToken] = useState<string | null>(() => {
    return TokenManager.getToken();
  });
  const [isLoading, setIsLoading] = useState(true);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState<boolean>(
    TokenManager.isImpersonating()
  );
  const [originalUser, setOriginalUser] = useState<User | null>(() => {
    return TokenManager.getOriginalUser<User>();
  });
  const [originalToken, setOriginalToken] = useState<string | null>(() => {
    return TokenManager.getOriginalToken();
  });

  const isAuthenticated = !!token && !!user;

  // CR-5275: extracted from login() so finalizeMfaLogin (called after the
  // MFA setup-confirm or login-verify endpoint succeeds) can land the
  // user in the exact same authenticated state as a single-step login.
  const persistAuthCredentials = (userData: User, authToken: string) => {
    if (userData.platform && !userData.company_type) {
      userData.company_type = userData.platform.toLowerCase() as User['company_type'];
    }
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);
    TokenManager.stopImpersonation();
    Cache.clearAll();
    Cache.setUserScope(userData.id);
    setUser(userData);
    setToken(authToken);
    TokenManager.setToken(authToken);
    TokenManager.setUser(userData);
  };

  const login = async (email: string, password: string, rememberMe = false): Promise<LoginResult> => {
    try {
      // Set storage preference before storing the token
      TokenManager.setRememberMe(rememberMe);
      const response = await authAPI.login(email, password);
      // API returns standardized format: { status, message, data: ... }
      // CR-5275: data may be { user, token } (no MFA), or one of the MFA
      // branches. Detect by inspecting flags before destructuring.
      const data = response.data.data as {
        user?: User; token?: string;
        mfa_setup_required?: boolean; mfa_required?: boolean;
        mfa_session_token?: string;
      };
      if (data.mfa_setup_required && data.mfa_session_token) {
        return { kind: 'mfa_setup_required', sessionToken: data.mfa_session_token };
      }
      if (data.mfa_required && data.mfa_session_token) {
        return { kind: 'mfa_required', sessionToken: data.mfa_session_token };
      }
      if (data.user && data.token) {
        persistAuthCredentials(data.user, data.token);
        return { kind: 'success' };
      }
      throw new Error('Unexpected login response payload');
    } catch (error: unknown) {
      console.error('Login error:', error);
      if (error instanceof Error && 'response' in error) {
        console.error('Error response:', (error as { response?: { data?: unknown } }).response?.data);
      }
      throw error;
    }
  };

  const finalizeMfaLogin = (userData: User, authToken: string) => {
    persistAuthCredentials(userData, authToken);
  };

  const logout = () => {
    // Read the token and hand it to the logout call explicitly, before any
    // of the clearing below. This used to call authAPI.logout() after
    // TokenManager.clearAll(); the request interceptor looks the token up
    // when axios runs it on a microtask, by which point it was gone, so the
    // call went out anonymous, the server answered 401 and the catch swallowed
    // it. The token stayed valid — the session was only ever torn down in the
    // browser. That also meant LogoutView's terminate_impersonation_chains()
    // never ran, so the admin's parked full-privilege token outlived the
    // logout, which is the exact CWE-613 case that view exists to prevent.
    // Ordering alone would not have fixed it: the interceptor is async, so the
    // synchronous clearAll() below still wins that race.
    //
    // Deliberately not awaited: the local session is dropped immediately
    // either way, so a slow or failed network call can never strand the user
    // on a page they have signed out of.
    const activeToken = TokenManager.getToken() ?? undefined;
    const serverLogout = authAPI.logout(activeToken).catch(() => {
      // Best effort — the local session is cleared regardless.
    });

    setUser(null);
    setToken(null);

    // Clear impersonation state
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);

    // Clear all secure storage
    TokenManager.clearAll();

    // Clear page-level cache and reset user scope
    Cache.clearAll();
    Cache.setUserScope(null);

    return serverLogout;
  };

  const startImpersonation = (
    targetUser: User | ImpersonatableUser,
    targetToken: string,
    originalTok: string,
    targetContextCompany?: { id: number; name: string },
    auditLogId?: number,
    targetContextGroup?: { id: number; name: string },
  ) => {
    // Derive company_type from platform if not present (same as login flow)
    const userWithType = { ...targetUser } as User;
    if (userWithType.platform && !userWithType.company_type) {
      userWithType.company_type = userWithType.platform.toLowerCase() as User['company_type'];
    }

    // Clear all caches before switching users
    Cache.clearAll();
    Cache.setUserScope(userWithType.id);
    // ViewContext persists portalMode/selectedCompany in its own localStorage
    // key; if we don't clear it the impersonated user inherits the admin's
    // sidebar + nav until they manually switch portals.
    localStorage.removeItem('helix_view_context_cache');

    // Persist the impersonation state BEFORE reload so it survives
    TokenManager.setToken(targetToken);
    TokenManager.setUser(userWithType);
    if (user) {
      TokenManager.startImpersonation(user, originalTok, auditLogId);
    }
    // CR-4858: Pin the access-context company (if the caller knew one) so the
    // post-reload ViewContext lands on the company the admin was inspecting,
    // not the impersonated user's primary company.
    if (targetContextCompany) {
      TokenManager.setImpersonationContext(targetContextCompany);
    }
    // CR-5316 rework: Admin invoked Login As from a Supplier Group page →
    // pin a GROUP-scoped landing instead of a site/company so the
    // impersonated session lands at the group view-level the admin was
    // inspecting, not at one of its constituent sites.
    if (targetContextGroup) {
      TokenManager.setImpersonationGroupContext(targetContextGroup);
    }

    // Route the impersonation session to the correct landing page. The
    // previous hard-coded /purchasing broke for compliance-only tenants
    // (MAB) where the supplier side never has a Purchasing menu. We now
    // pick a destination based on the target user's role + approval state,
    // and the Login.tsx effect will honour the tenant's
    // supplier_default_landing_path on first paint after the reload.
    const role = (userWithType.company_type || userWithType.platform?.toLowerCase() || 'customer').toLowerCase();
    let dest = '/dashboard';
    if (role === 'logistics') {
      dest = '/logistics/call-offs';
    } else if (role === 'supplier') {
      // Pre-approval suppliers go straight to the onboarding progress page;
      // approved suppliers follow the tenant setting, which is applied by
      // the Login page redirect after settings finish loading.
      dest = userWithType.supplier_is_approved === false
        ? '/onboarding/my-progress'
        : '/login';
    }
    window.location.href = dest;
  };

  const stopImpersonation = async () => {
    if (!originalToken) {
      console.error('No original token found');
      return;
    }

    try {
      const auditLogId = TokenManager.getAuditLogId() ?? undefined;
      const response = await authAPI.stopImpersonate(originalToken, auditLogId);
      // API returns standardized format: { status, message, data: { user, token } }
      const { user: restoredUser, token: restoredToken } = response.data.data;

      // Clear all caches
      Cache.clearAll();
      Cache.setUserScope(restoredUser.id);
      localStorage.removeItem('helix_view_context_cache');

      // Persist restored user BEFORE reload
      TokenManager.setToken(restoredToken);
      TokenManager.setUser(restoredUser);
      TokenManager.stopImpersonation();

      // Force a full page reload to clear all stale impersonation state
      window.location.href = '/dashboard';
    } catch (error) {
      // Never strand the user. This used to rethrow, and the banner's click
      // handler had nothing to catch it: a failed stop (parked admin token
      // gone — a second "Login As" elsewhere, or a logout on either side)
      // left the admin trapped as the target. The banner stayed, /login
      // bounced straight back into the app, and only clearing browser
      // storage got them out. The server now recovers the admin itself
      // where it legitimately can; when it cannot, end the session cleanly
      // and let them sign in again.
      console.error('Error stopping impersonation:', error);
      setUser(null);
      setToken(null);
      setIsImpersonating(false);
      setOriginalUser(null);
      setOriginalToken(null);
      TokenManager.clearAll();
      Cache.clearAll();
      Cache.setUserScope(null);
      localStorage.removeItem('helix_view_context_cache');
      window.location.href = '/login';
    }
  };

  /**
   * Merges a partial update into the signed-in user and persists it.
   *
   * For flows where the API already returns the authoritative value (e.g. legal
   * acceptance): checkAuthStatus deliberately skips its network call while
   * impersonating, so a caller that relies on it to observe its own write never
   * sees the change and re-renders on stale state.
   *
   * @param patch - Fields to merge into the current user
   */
  const updateUser = (patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      TokenManager.setUser(next);
      return next;
    });
  };

  const checkAuthStatus = async () => {
    let activeToken = token;
    let activeUser = user;
    let activeImpersonating = isImpersonating;

    // CR-6282: a tab opened from a dashboard drill-down PO/GRN hyperlink
    // (target="_blank") starts with an empty sessionStorage, so it has no token
    // and would be bounced to /login — which the user perceives as a forced
    // sign-out. Before giving up, ask any already-open tab to hand over the live
    // session over BroadcastChannel (in-memory, same-origin, never persisted).
    // isLoading stays true throughout, so ProtectedRoute shows the spinner
    // rather than flashing the login page.
    if (!activeToken) {
      const snapshot = await requestSharedSession();
      if (TokenManager.importSession(snapshot)) {
        activeToken = TokenManager.getToken();
        activeUser = TokenManager.getUser<User>();
        activeImpersonating = TokenManager.isImpersonating();
        if (activeUser?.platform && !activeUser.company_type) {
          activeUser.company_type = activeUser.platform.toLowerCase() as User['company_type'];
        }
        setToken(activeToken);
        setUser(activeUser);
        setIsImpersonating(activeImpersonating);
        setOriginalUser(TokenManager.getOriginalUser<User>());
        setOriginalToken(TokenManager.getOriginalToken());
        if (activeUser?.id) Cache.setUserScope(activeUser.id);
      }
    }

    if (!activeToken) {
      setIsLoading(false);
      return;
    }

    // During impersonation, trust the persisted user/token from TokenManager.
    // The /auth/status/ endpoint uses SessionAuthentication (which has Mark's
    // session cookie) before TokenAuthentication, so it returns the original
    // user instead of the impersonated user. Skip the API call to avoid this.
    if (activeImpersonating && activeUser) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.status();
      // API returns standardized format: { status, data: { authenticated, user } }
      const statusData = response.data.data;
      if (statusData.authenticated && statusData.user) {
        // Derive company_type from platform if not present
        if (statusData.user.platform && !statusData.user.company_type) {
          statusData.user.company_type = statusData.user.platform.toLowerCase();
        }
        setUser(statusData.user);
        TokenManager.setUser(statusData.user);
        Cache.setUserScope(statusData.user.id);
      } else {
        // Token is invalid
        logout();
      }
    } catch (error) {
      // Token is invalid or network error
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CR-6282: answer session-handoff requests from tabs opened via drill-down
  // target="_blank" links. serveSharedSession reads the live session from
  // TokenManager at request time, so it always serves the current token and
  // only responds when this tab is authenticated.
  useEffect(() => serveSharedSession(), []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    finalizeMfaLogin,
    logout,
    checkAuthStatus,
    updateUser,
    // Impersonation
    isImpersonating,
    originalUser,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
